import * as React from "react";
import { QESButton, QESMultiSelect } from "@app/shared/qes-standards";
import { orderBy } from "lodash";
import { AdminUsersContext } from "@app/admin/contexts/AdminUsersContext";
import { RoleModel } from "@app/user";
import { ClientRoleModel } from "@app/client/index";
import { MultiselectItem, MultiselectOnChangeResponse } from "@app/shared/qes-standards/forms/Multiselect";
import { CheckboxStateEnum } from "../../../shared/qes-standards/forms/Checkbox";
import { RoleGroupModel } from "@app/admin/role-groups";
import { ColumnConfig, Waffles, wafflesKeyColumnName } from "@app/shared/waffles";
import { AccordionContainer, Accordion } from "@app/shared";
import { JSX } from "react";

interface RolePreviewTableData {
  [wafflesKeyColumnName]: string;
  role: string;
  assignedBy: JSX.Element;
}

const rolePreviewColumnConfig: ColumnConfig = {
  assignedBy: {
    displayText: "Assigned By",
    testId: "assignedBy",
  },
  role: {
    displayText: "Role",
    testId: "role"
  },
};

interface UserRolesProps {
  disabled?: boolean;
}

export const UserRoles: React.FC<React.PropsWithChildren<UserRolesProps>> = (props: UserRolesProps) => {
  const adminUsersContext = React.useContext(AdminUsersContext);

  const selectedRoleGroupOptions = React.useMemo(() => orderBy((adminUsersContext.roleGroups as RoleGroupModel[]).map((roleGroup) => roleGroup.name), (rg) => rg.toLowerCase(), "asc"), [adminUsersContext.roleGroups]);
  const selectedRoleOptions = React.useMemo(() => orderBy((adminUsersContext.roles as RoleModel[]).map((role) => role.name), (r) => r.toLowerCase(), "asc"), [adminUsersContext.roles]);

  const roleGroupOptions = React.useMemo(() => {
    const options: { [key: string]: MultiselectItem } = {};
    if (adminUsersContext.allowedRoleGroups) {
      adminUsersContext.allowedRoleGroups.forEach((r) => Object.assign(options,
        {
          [r.name]: {
            currentState: selectedRoleGroupOptions.find((so) => so === r.name) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
            displayValue: r.name
          }
        }));
    }
    return options;
  }, [adminUsersContext.allowedRoleGroups, selectedRoleGroupOptions]);

  const roleOptions = React.useMemo(() => {
    const roleOptions: { [key: string]: MultiselectItem } = {};
    if (adminUsersContext.allowedRoles) {
      adminUsersContext.allowedRoles.forEach((r) => Object.assign(roleOptions,
        {
          [r.name]: {
            currentState: selectedRoleOptions.find((so) => so === r.name) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
            displayValue: r.name
          }
        }));
    }
    return roleOptions;
  }, [adminUsersContext.allowedRoles, selectedRoleOptions]);

  const rolePreview = React.useMemo(() => {
    const roleDict: { [key: number]: { assignedBy: string[], id: number, name: string } } = {};
    for (const role of adminUsersContext.roles) {
      roleDict[role.roleId] = { assignedBy: ["Individually Assigned"], id: role.roleId, name: role.name };
    }

    const selectedRoleGroups =
      orderBy(
        adminUsersContext.allowedRoleGroups.filter(x => adminUsersContext.roleGroups.some(y => x.roleGroupId === y.roleGroupId)),
        x => x.name, "asc");
    for (const roleGroup of selectedRoleGroups) {
      for (const role of roleGroup.roles) {
        if (!!roleDict[role.roleId]) {
          roleDict[role.roleId].assignedBy.push(roleGroup.name);
        } else {
          roleDict[role.roleId] = { assignedBy: [roleGroup.name], id: role.roleId, name: role.name };
        }
      }
    }

    const preview: RolePreviewTableData[] = orderBy(Object.values(roleDict), rd => rd.name, "asc").map(rd => {
      /* eslint-disable sort-keys */
      return {
        key: rd.id.toString(),
        role: rd.name,
        assignedBy:
          <>
            {rd.assignedBy.map((x, i) => <div key={i}>{x}<br /></div>)}
          </>
      }
      /* eslint-enable sort-keys */
    });

    return preview;
  }, [adminUsersContext.roleGroups, adminUsersContext.roles]);

  const updateRoleGroups = (response: MultiselectOnChangeResponse) => {
    const selectedRoleGroups = response.checkedKeys;
    const newRoleGroups = selectedRoleGroups.map((roleGroup) => {
      const matchedRoleGroupModel = adminUsersContext.allowedRoleGroups.find((rg) => rg.name === roleGroup);
      return matchedRoleGroupModel;
    });
    adminUsersContext.setRoleGroups(orderBy(newRoleGroups, (n) => n.name.toLowerCase(), "asc"));
  }

  const updateRoles = (response: MultiselectOnChangeResponse) => {
    const selectedRoles = response.checkedKeys;
    const newRoles = selectedRoles.map((role) => {
      const matchedRoleModel = adminUsersContext.allowedRoles.find((r) => r.name === role);
      return matchedRoleModel;
    });
    adminUsersContext.setRoles(orderBy(newRoles, (n) => n.name.toLowerCase(), "asc"));
  }

  const onClearAll = (setterFunc: React.Dispatch<React.SetStateAction<ClientRoleModel[]>>) => {
    setterFunc(Array<ClientRoleModel>());
  }

  const onSelectAll = (source: ClientRoleModel[], setterFunc: React.Dispatch<React.SetStateAction<ClientRoleModel[]>>) => {
    const newRoles = source.map((r) => r);
    setterFunc(orderBy(newRoles, (nr) => nr.name, "asc"));
  }

  const onTranscribe = (allOptions: ClientRoleModel[],
    source: ClientRoleModel[],
    setterFunc: React.Dispatch<React.SetStateAction<ClientRoleModel[]>>) => {
    const newRoles = source.map((sourceItem) => {
      const matchedRoleModel = allOptions.find((r) => r.name === sourceItem.name);
      return matchedRoleModel;
    }).filter((r) => !!r);
    setterFunc(orderBy(newRoles, (nr) => nr.name, "asc"));
  }

  return (
    <div className="col-8 pl-0">
      <QESMultiSelect
        containerClassName="mb-3"
        disabled={props.disabled}
        label="Authorized Role Groups"
        onChange={updateRoleGroups}
        options={roleGroupOptions}
        prefix="roleGroups"
        testId="admin-users__role-groups"
        validationTooltipContent="User must be in at least 1 role either directly or via a role group."
      />
      <QESMultiSelect
        containerClassName="mb-3"
        disabled={props.disabled}
        errorText="At least one role must be selected."
        label="Individually Assigned Roles"
        onChange={updateRoles}
        options={roleOptions}
        required={adminUsersContext.requiredFields["roles"]}
        isValid={adminUsersContext.validationObject["roles"]}
        prefix="roles"
        testId="admin-users__roles"
        validationTooltipContent={`User must be in at least 1 role either directly or via a role group".`}
      />
      <QESButton
        qesTestId="admin-users__roles-SelectAll"
        qesType="secondary"
        onClick={() => onSelectAll(adminUsersContext.allowedRoles, adminUsersContext.setRoles)}>
        Select All</QESButton>
      <QESButton
        qesTestId="admin-users__roles-ClearAll"
        qesType="secondary"
        onClick={() => onClearAll(adminUsersContext.setRoles)}>
        Clear All</QESButton>
      <QESButton
        qesTestId="admin-users__roles-SelectDefaults"
        qesType="secondary"
        onClick={() => onTranscribe(adminUsersContext.allowedRoles, adminUsersContext.defaultUserRoles, adminUsersContext.setRoles)}>
        Use Default User Roles</QESButton>
      <AccordionContainer className="mt-3">
        <Accordion title="Role Preview" testId="user-roles__role-preview-accordion">
          <Waffles<RolePreviewTableData>
            columns={rolePreviewColumnConfig}
            data={rolePreview}
            fullWidth={true}
            testId="role-preview-waffles-table"
            emptyTableMessage="There are no roles selected for this user."
          />
        </Accordion>
      </AccordionContainer>
    </div>
  );
}
